import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError, resolveMedia, isPdfUrl } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Upload, Trash2, FileText } from "lucide-react";
import MediaUpload from "@/components/MediaUpload";
import RichTextEditor from "@/components/RichTextEditor";

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="block text-xs text-neutral-500 mb-1.5">{label}</span>
      {children}
      {hint && <span className="text-xs text-neutral-400 mt-1 block">{hint}</span>}
    </label>
  );
}
function Input(props) {
  return <input {...props} className={`w-full border border-brand-line px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-strong))] focus:ring-1 focus:ring-[hsl(var(--brand-green-strong))] ${props.className || ""}`} />;
}
function Textarea(props) {
  return <textarea {...props} className={`w-full border border-brand-line px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-strong))] focus:ring-1 focus:ring-[hsl(var(--brand-green-strong))] min-h-[180px] ${props.className || ""}`} />;
}

export default function AdminNewsEdit() {
  const { id } = useParams();
  const isNew = !id;
  const nav = useNavigate();
  const [form, setForm] = useState({
    title: "", teaser: "", body: "", image_url: "", image_preview_url: "", image_caption: "", author: "Stadt Detmold", status: "published", date: new Date().toISOString().slice(0, 10), media: [],
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    api.get("/news").then(({ data }) => {
      const item = (data || []).find((n) => n.id === id);
      if (item) {
        setForm({
          title: item.title || "",
          teaser: item.teaser || "",
          body: item.body || "",
          image_url: item.image_url || "",
          image_preview_url: item.image_preview_url || "",
          image_caption: item.image_caption || "",
          author: item.author || "",
          status: item.status || "published",
          date: (item.date || new Date().toISOString()).slice(0, 10),
          media: item.media || [],
        });
      }
    }).finally(() => setLoading(false));
  }, [id, isNew]);

  async function uploadImage(e) {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, image_url: data.url, image_preview_url: data.preview_url || "" }));
      toast.success(data.kind === "pdf" ? "PDF hochgeladen — Vorschau erstellt" : "Datei hochgeladen");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, date: new Date(form.date).toISOString() };
      if (isNew) { await api.post("/news", payload); toast.success("News angelegt"); }
      else { await api.put(`/news/${id}`, payload); toast.success("Gespeichert"); }
      nav("/admin/news");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  if (loading) return <p className="text-sm text-neutral-500">Lade…</p>;

  return (
    <form onSubmit={save} className="space-y-6 pb-16" data-testid="admin-news-edit">
      <Link to="/admin/news" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900 mb-2"><ArrowLeft className="h-4 w-4" /> Zurück</Link>
      <h1 className="font-semibold text-3xl">{isNew ? "Neue News anlegen" : "News bearbeiten"}</h1>

      <div className="bg-white border border-brand-line p-8 space-y-5">
        <Field label="Titel"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="news-edit-title" /></Field>
        <Field label="Kurzbeschreibung / Teaser"><Textarea value={form.teaser} onChange={(e) => setForm({ ...form, teaser: e.target.value })} className="min-h-[80px]" data-testid="news-edit-teaser" /></Field>
        <Field label="Beitragsbild oder PDF" hint="Bild (JPG/PNG/WEBP) oder PDF — bei PDF wird automatisch die erste Seite als Vorschau erzeugt.">
          {form.image_url && (
            isPdfUrl(form.image_url) ? (
              <div className="mb-2 inline-block">
                <a href={resolveMedia(form.image_url)} target="_blank" rel="noreferrer" className="relative block border border-brand-line hover:border-[hsl(var(--brand-green))] group">
                  {form.image_preview_url ? (
                    <img src={resolveMedia(form.image_preview_url)} alt="PDF-Vorschau" className="w-full max-w-md aspect-[16/9] object-cover" />
                  ) : (
                    <div className="w-full max-w-md aspect-[16/9] bg-neutral-50 flex flex-col items-center justify-center gap-2 text-neutral-500">
                      <FileText className="h-10 w-10" strokeWidth={1.5} />
                      <span className="text-xs">PDF — Vorschau wird beim nächsten Upload erzeugt</span>
                    </div>
                  )}
                  <span className="absolute top-2 left-2 bg-[hsl(var(--brand-green-dark))] text-white text-[10px] uppercase tracking-wider px-2 py-0.5 inline-flex items-center gap-1">
                    <FileText className="h-3 w-3" /> PDF
                  </span>
                </a>
              </div>
            ) : (
              <img src={resolveMedia(form.image_url)} alt="" className="w-full max-w-md aspect-[16/9] object-cover border border-brand-line mb-2" />
            )
          )}
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2 border border-brand-line px-3 py-2 text-xs cursor-pointer hover:bg-neutral-50">
              <Upload className="h-3.5 w-3.5" /> {form.image_url ? "Ersetzen" : "Datei hochladen"}
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={uploadImage} />
            </label>
            {form.image_url && <button type="button" onClick={() => setForm({ ...form, image_url: "", image_preview_url: "" })} className="text-xs text-neutral-500 hover:text-red-600 inline-flex items-center gap-1"><Trash2 className="h-3.5 w-3.5" /> Entfernen</button>}
          </div>
        </Field>
        <Field label="Bildunterschrift (optional)"><Input value={form.image_caption} onChange={(e) => setForm({ ...form, image_caption: e.target.value })} /></Field>
        <Field label="Haupttext" hint="Mit dem Editor formatieren: Absätze, Fettungen, Listen, Links, Überschriften.">
          <RichTextEditor
            value={form.body}
            onChange={(html) => setForm({ ...form, body: html })}
            minHeight={260}
            placeholder="Haupttext eingeben…"
            testid="news-edit-body"
          />
        </Field>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Datum"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required /></Field>
          <Field label="Autor / Herausgeber"><Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} /></Field>
          <Field label="Status">
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="news-edit-status" className="w-full border border-brand-line px-3 py-2.5 text-sm focus:outline-none">
              <option value="published">Veröffentlicht</option>
              <option value="draft">Entwurf</option>
            </select>
          </Field>
        </div>
      </div>

      <div className="bg-white border border-brand-line p-8">
        <h3 className="font-semibold text-lg mb-2">Weitere Medien</h3>
        <p className="text-xs text-neutral-500 mb-4">Optional: weitere Bilder, Videos oder PDFs zum Beitrag hinzufügen. Diese werden in der Detailansicht als Galerie mit Lightbox angezeigt.</p>
        <MediaUpload value={form.media} onChange={(media) => setForm({ ...form, media })} testid="news-media-upload" />
      </div>

      <div className="flex gap-3 justify-end">
        <Link to="/admin/news" className="border border-brand-line px-5 py-2.5 text-sm hover:bg-neutral-50">Abbrechen</Link>
        <button type="submit" disabled={saving} data-testid="news-edit-save" className="bg-[hsl(var(--brand-green-dark))] text-white px-6 py-2.5 text-sm hover:bg-[hsl(var(--brand-green-strong))] disabled:opacity-50">
          {saving ? "Speichern…" : "Speichern"}
        </button>
      </div>
    </form>
  );
}
